import { useCallback, useEffect, useState } from "react";
import { UserPlus, Trash2, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import { getMembers, inviteMember, removeMember, errorText } from "@/lib/family";

const ROLE = { owner: "Parent", parent: "Parent", grandparent: "Grandparent" };

const InviteResult = ({ inv }) => {
  const copy = () => navigator.clipboard?.writeText(inv.link).then(() => toast.success("Invite link copied"));
  return (
    <div className="pp-feedback small mt-3" data-testid="invite-result">
      <p><Mail className="inline h-4 w-4" /> Invite sent! Or share code <b data-testid="invite-code">{inv.code}</b> (valid {inv.expires_days} days).</p>
      <button type="button" className="pp-ghost mt-2" onClick={copy} data-testid="invite-copy-link"><Copy className="h-4 w-4" /> Copy invite link</button>
    </div>
  );
};

export const FamilyMembers = ({ pin }) => {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("parent");
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getMembers(pin).then(setList).catch(() => setList([])), [pin]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setInv(await inviteMember(pin, email.trim(), role)); setEmail(""); } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from your family?`)) return;
    try { await removeMember(pin, m.id); toast.success("Removed from family"); load(); } catch (err) { toast.error(errorText(err)); }
  };

  return (
    <section className="pp-card" data-testid="family-members">
      <p className="pp-label">Grown-ups in this family</p>
      <h2 className="pp-h2">Parents & grandparents</h2>
      <ul className="grid gap-2 mt-3">
        {list.map((m) => (
          <li key={m.id} className="pp-child-row" data-testid={`member-row-${m.id}`}>
            <b>{m.name || m.email}</b><span className="pp-badge text-xs">{ROLE[m.role] || "Parent"}{m.is_you ? " · you" : ""}</span>
            <span className="text-xs opacity-70 truncate">{m.email}</span>
            {!m.is_you && m.role !== "owner" && list.find((x) => x.is_you)?.role === "owner" && <button className="pp-icon ml-auto" onClick={() => remove(m)} aria-label={`Remove ${m.name}`} data-testid={`member-remove-${m.id}`}><Trash2 className="h-4 w-4" /></button>}
          </li>
        ))}
      </ul>
      <form onSubmit={send} className="flex flex-wrap gap-2 items-center mt-4" data-testid="invite-form">
        <input className="pp-input flex-1 min-w-0" style={{ marginTop: 0, minWidth: 200 }} type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="Their email" data-testid="invite-email-input" />
        <select className="pp-voice-select" value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role-select">
          <option value="parent">Second parent</option>
          <option value="grandparent">Grandparent</option>
        </select>
        <button className="pp-primary" disabled={busy || !email.trim()} data-testid="invite-send-button"><UserPlus className="h-5 w-5" /> Invite</button>
      </form>
      <p className="text-xs opacity-70 mt-2">They'll need their own NICE membership. Share your parent PIN with them yourself so they can open the Family Area.</p>
      {inv && <InviteResult inv={inv} />}
    </section>
  );
};
